'use client'

import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'

type AnimationVariant =
  | 'fadeInUp'
  | 'fadeIn'
  | 'scaleIn'
  | 'slideInLeft'
  | 'slideInRight'
  | 'stagger'

interface SectionAnimationWrapperProps {
  children: ReactNode
  variant?: AnimationVariant
  delay?: number
  duration?: number
  className?: string
  staggerDelay?: number
  direction?: 'left' | 'right'
}

const animationVariants: Record<AnimationVariant, Variants> = {
  fadeInUp: {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
        mass: 0.8,
        duration: 0.8,
      },
    },
  },
  fadeIn: {
    hidden: {
      opacity: 0,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 120,
        damping: 25,
        mass: 0.6,
        duration: 0.7,
      },
    },
  },
  scaleIn: {
    hidden: {
      opacity: 0,
      scale: 0.85,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 150,
        damping: 20,
        mass: 0.7,
        duration: 0.8,
      },
    },
  },
  slideInLeft: {
    hidden: {
      opacity: 0,
      x: -80,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
        mass: 0.8,
        duration: 0.8,
      },
    },
  },
  slideInRight: {
    hidden: {
      opacity: 0,
      x: 80,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
        mass: 0.8,
        duration: 0.8,
      },
    },
  },
  stagger: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  },
}

const staggerItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 120,
      damping: 22,
      mass: 0.7,
      duration: 0.6,
    },
  },
}

export function SectionAnimationWrapper({
  children,
  variant = 'fadeInUp',
  delay = 0,
  duration,
  className,
  staggerDelay = 0.1,
  direction,
}: SectionAnimationWrapperProps) {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: '0px 0px -50px 0px',
  })

  const shouldReduceMotion = useReducedMotion()

  // Override variant if direction is specified
  const finalVariant = direction
    ? direction === 'left'
      ? 'slideInLeft'
      : 'slideInRight'
    : variant

  const variants = animationVariants[finalVariant]

  // If user prefers reduced motion, use simplified animations
  const reducedMotionVariants: Variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  }

  // Use reduced motion variants if user prefers reduced motion
  const finalVariants = shouldReduceMotion ? reducedMotionVariants : variants

  // Type-safe access to visible state and transition
  const visibleState =
    finalVariants.visible && typeof finalVariants.visible === 'object'
      ? (finalVariants.visible as Record<string, unknown>)
      : {}
  const existingTransition =
    'transition' in visibleState &&
    visibleState.transition &&
    typeof visibleState.transition === 'object' &&
    !Array.isArray(visibleState.transition)
      ? (visibleState.transition as Record<string, unknown>)
      : {}

  // Apply custom duration if provided
  const customVariants: Variants = duration
    ? {
        ...finalVariants,
        visible: {
          ...visibleState,
          transition: {
            ...existingTransition,
            ...(shouldReduceMotion
              ? { duration }
              : {
                  duration,
                  delay: delay,
                  ...(finalVariant === 'stagger' && {
                    staggerChildren: staggerDelay,
                    delayChildren: delay,
                  }),
                }),
          },
        },
      }
    : {
        ...finalVariants,
        visible: {
          ...visibleState,
          transition: {
            ...existingTransition,
            ...(shouldReduceMotion
              ? {}
              : {
                  delay: delay,
                  ...(finalVariant === 'stagger' && {
                    staggerChildren: staggerDelay,
                    delayChildren: delay,
                  }),
                }),
          },
        },
      }

  // For stagger variant, wrap children in motion.div with staggerItemVariants
  if (
    finalVariant === 'stagger' &&
    Array.isArray(children) &&
    !shouldReduceMotion
  ) {
    return (
      <motion.div
        ref={ref}
        initial='hidden'
        animate={inView ? 'visible' : 'hidden'}
        variants={customVariants}
        className={className}
        style={{
          willChange: inView ? 'transform, opacity' : 'auto',
        }}
      >
        {children.map((child, index) => (
          <motion.div key={index} variants={staggerItemVariants}>
            {child}
          </motion.div>
        ))}
      </motion.div>
    )
  }

  return (
    <motion.div
      ref={ref}
      initial='hidden'
      animate={inView ? 'visible' : 'hidden'}
      variants={customVariants}
      className={className}
      style={{
        willChange: inView ? 'transform, opacity' : 'auto',
      }}
    >
      {children}
    </motion.div>
  )
}
